import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_PATHS } from '../config/api-paths';
import {
  AuctionRankingConfig,
  Chapter4ConfigApi,
  FinancingScoringConfig,
  ProductConfigurationDTO,
  TimerConfig,
} from '../tokens/chapter4-config.token';

/** The `{ data: [...] }` envelope both catalogue endpoints answer with. */
interface DataEnvelope<T> {
  data: T[];
}

/**
 * REST-only implementation of `Chapter4ConfigApi` (EM-1679).
 *
 * Calls the five `embedded` endpoints behind the BRS Chapter-4 Master-Admin configuration screens plus
 * the SME-facing catalogue read. Rule 07 / Rule 10 — no mock fallback anywhere; Playwright intercepts at
 * the HTTP layer with `page.route()`, unit specs use inline spies.
 *
 * Authority: every `master-admin/**` path requires `ROLE_MASTER_ADMIN` server-side (and the portal's
 * `roleGuard` on the route); `FINANCING_PRODUCTS` needs only an authenticated session.
 */
@Injectable()
export class Chapter4ConfigRestService implements Chapter4ConfigApi {
  private readonly http = inject(HttpClient);

  listProductConfig(): Observable<ProductConfigurationDTO[]> {
    return this.http
      .get<DataEnvelope<ProductConfigurationDTO>>(API_PATHS.MASTER_ADMIN_PRODUCT_CONFIG)
      .pipe(map(response => response?.data ?? []));
  }

  updateProductConfig(rows: ProductConfigurationDTO[]): Observable<ProductConfigurationDTO[]> {
    return this.http
      .put<DataEnvelope<ProductConfigurationDTO>>(API_PATHS.MASTER_ADMIN_PRODUCT_CONFIG, rows)
      .pipe(map(response => response?.data ?? []));
  }

  listActiveProducts(): Observable<ProductConfigurationDTO[]> {
    return this.http
      .get<DataEnvelope<ProductConfigurationDTO>>(API_PATHS.FINANCING_PRODUCTS)
      .pipe(map(response => response?.data ?? []));
  }

  getScoringConfig(): Observable<FinancingScoringConfig> {
    return this.http.get<FinancingScoringConfig>(API_PATHS.MASTER_ADMIN_FINANCING_SCORING);
  }

  updateScoringConfig(config: FinancingScoringConfig): Observable<FinancingScoringConfig> {
    return this.http.put<FinancingScoringConfig>(API_PATHS.MASTER_ADMIN_FINANCING_SCORING, config);
  }

  getAuctionConfig(): Observable<AuctionRankingConfig> {
    return this.http.get<AuctionRankingConfig>(API_PATHS.MASTER_ADMIN_AUCTION_CONFIG);
  }

  updateAuctionConfig(config: AuctionRankingConfig): Observable<AuctionRankingConfig> {
    return this.http.put<AuctionRankingConfig>(API_PATHS.MASTER_ADMIN_AUCTION_CONFIG, config);
  }

  getTimerConfig(): Observable<TimerConfig> {
    return this.http.get<TimerConfig>(API_PATHS.MASTER_ADMIN_TIMER_CONFIG);
  }

  updateTimerConfig(config: TimerConfig): Observable<TimerConfig> {
    return this.http.put<TimerConfig>(API_PATHS.MASTER_ADMIN_TIMER_CONFIG, config);
  }
}
